import { Globe, ScanLine, Waypoints } from "@suite/components/icons";
import { Card } from "@suite/components/ui/card";
import { cn } from "@suite/lib/utils";
import type { IceCandidateRecord } from "@suite/lib/webrtc";
import IceCandidateRow from "./IceCandidateRow";

/* The probe state of the WebRTC Leak Test.
 *
 * Not a spinner: it is the result's own frame with the two addresses still missing. The
 * verdict slot, the comparison columns, the glyph between them, the local-network line and
 * the log card all render at final size, so the answer fills slots that already exist.
 *
 * Hook-free, so the route-level loading.tsx (a Server Component) renders it bare, and the
 * client instrument renders the same markup while probing, with one upgrade the router can't
 * do: candidates stream into the log as the browser emits them. */

const HEADER = "font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70";

const LOG_ROWS = ["l1", "l2", "l3"];

interface BarProps {
  className?: string;
}

/** A value that hasn't arrived yet, in the shared skeleton language. */
function Bar({ className }: BarProps) {
  return <span aria-hidden className={cn("skeleton-bar block rounded-full", className)} />;
}

/**
 * Reserved boxes matching the LINE BOX of the text each bar stands in for, with the bar centred
 * inside. Measured against the result frame: the address renders at 24px/30px with `mt-2.5`, the
 * geo line at 13px/18.57px with `mt-1.5`.
 *
 * The bars themselves stay deliberately thinner than the text — that is the skeleton language —
 * so the slot has to carry the height instead. Sizing the bars to the text would fix the
 * geometry but make the loading state read as a solid block. Without these, the plaque measured
 * 122px scanning against 128.6px settled, and the verdict badge (centred in a stretch column)
 * visibly dropped ~3px the instant the result landed.
 */
const ADDRESS_SLOT = "mt-2.5 flex h-[30px] items-center";
const GEO_SLOT = "mt-1.5 flex h-[18.57px] items-center";

/**
 * Which stage of the run this chassis is standing in for. Exported and imported by the tool, so
 * the two files cannot end up with different ideas of how many stages there are.
 *
 * `slow` is the honest version of a wait: the browser has not signalled that it finished, which
 * with a tunnel interface up it may never do. Until this existed, the tool published a verdict of
 * "the test couldn't finish" at three seconds and then sat on it while the probe kept running.
 */
export type ScanPhase = "probing" | "slow" | "verifying";

/** One place for the wait's wording. Written to a similar length so the paragraph keeps its
 * wrapped line count and the footer below it does not move when the phase changes. */
const PHASE_COPY: Record<ScanPhase, { heading: string; body: string }> = {
  probing: {
    heading: "Checking for leaks…",
    body: "Gathering network paths and fetching your connection address, the same probe a tracking script would run.",
  },
  slow: {
    heading: "Still checking…",
    body: "Your browser hasn't said it finished, which is normal with a VPN or tunnel interface up. Still listening.",
  },
  verifying: {
    heading: "Verifying network…",
    body: "The addresses differ. Comparing their networks before reporting a leak.",
  },
};

interface Props {
  /** Candidates gathered so far, streamed into the log card while the probe runs. The
   * route-level loading.tsx passes nothing and renders the same chassis statically. */
  liveCandidates?: IceCandidateRecord[];
  /** A mismatch needs ASN evidence before it can safely become red or amber. */
  phase?: ScanPhase;
  /** Seconds elapsed, shown only once the wait is worth narrating. Omitted by the server-rendered
   * loading frame, which is why it cannot change the first paint. */
  elapsedSeconds?: number;
}

export default function WebRtcScanning({
  liveCandidates = [],
  phase = "probing",
  elapsedSeconds,
}: Props) {
  const ghostRows = Math.max(0, LOG_ROWS.length - liveCandidates.length);
  const copy = PHASE_COPY[phase];
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <Card className="relative gap-0 overflow-hidden p-0">
        {/* The same quiet texture the result carries: a fine dot grid, no light. */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(rgba(255,255,255,0.055)_1px,transparent_1px)] [mask-image:linear-gradient(to_bottom,black,transparent)] bg-[size:14px_14px]" />
        </div>

        <div className="relative flex flex-col gap-6 p-5 sm:p-7">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <ScanLine className="text-brand-green size-5" aria-hidden />
                <p className="text-2xl font-semibold tracking-tight text-white sm:text-[26px]">
                  {copy.heading}
                </p>
              </div>
            </div>
          </div>

          {/* The comparison, empty: the same split panel the result fills, one shared
           * hairline between the two readings, so the answer lands into geometry the eye has
           * already accepted. */}
          <div className="grid w-full divide-y divide-white/[0.08] rounded-xl bg-black/25 ring-1 ring-white/[0.07] sm:grid-cols-[1fr_auto_1fr] sm:divide-y-0">
            {/* The readings bookend the badge, matching the result's geometry: left reading
             * flush with the start edge, right reading mirrored against the end edge. */}
            <div className="min-w-0 p-4 sm:px-6 sm:py-5">
              <div className="w-full min-w-0">
                <div className="flex items-center gap-2">
                  <span className="bg-brand-green/10 text-brand-green flex size-6 shrink-0 items-center justify-center rounded-md">
                    <Globe className="size-3.5" aria-hidden />
                  </span>
                  <span className="font-mono text-[11px] font-semibold tracking-widest text-white/85 uppercase">
                    What sites see
                  </span>
                </div>
                <span className={ADDRESS_SLOT}>
                  <Bar className="h-6 w-52 max-w-full" />
                </span>
                <span className={GEO_SLOT}>
                  <Bar className="h-3 w-80 max-w-full" />
                </span>
              </div>
            </div>

            {/* The badge slot in its column, before the verdict fills it. The top seam
             * segment is fixed and the bottom one stretches, mirroring the result's badge
             * column exactly: the badge is pinned to the seam, so a result whose readings
             * outgrow the reserved slots below can't move it at handover. */}
            <div className="hidden sm:flex sm:flex-col sm:items-center sm:self-stretch sm:px-8">
              <span
                className="mt-4 h-[20.28px] w-px shrink-0 bg-gradient-to-b from-transparent to-white/[0.16]"
                aria-hidden
              />
              <span
                className="my-2 flex size-10 items-center justify-center rounded-full bg-white/[0.08] font-mono text-xl font-bold text-white/40 ring-4 ring-black/80 select-none"
                aria-hidden
              >
                ·
              </span>
              <span
                className="mb-4 w-px flex-1 bg-gradient-to-b from-white/[0.16] to-transparent"
                aria-hidden
              />
            </div>

            <div className="min-w-0 p-4 sm:px-6 sm:py-5">
              <div className="w-full min-w-0">
                <div className="flex items-center gap-2 sm:flex-row-reverse">
                  <span className="bg-brand-green/10 text-brand-green flex size-6 shrink-0 items-center justify-center rounded-md">
                    <Waypoints className="size-3.5" aria-hidden />
                  </span>
                  <span className="font-mono text-[11px] font-semibold tracking-widest text-white/85 uppercase">
                    What WebRTC reveals
                  </span>
                </div>
                <span className={cn(ADDRESS_SLOT, "sm:justify-end")}>
                  <Bar className="h-6 w-52 max-w-full" />
                </span>
                <span className={cn(GEO_SLOT, "sm:justify-end")}>
                  <Bar className="h-3 w-80 max-w-full" />
                </span>
              </div>
            </div>
          </div>

          {/* A floor of two lines, not a lock: the three variants are written to the same length
           * so a mid-run phase change keeps the same wrapped line count, but below ~375px the two
           * longer ones take three lines. The floor is what stops the footer moving at the widths
           * where they differ. */}
          <p className="min-h-[45.5px] text-sm leading-relaxed text-white/60">{copy.body}</p>

          <div className="flex flex-col gap-3 border-t border-white/[0.06] pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className={HEADER}>Local network</span>
              <Bar className="h-3 w-48 max-w-full" />
            </div>
            {/* Shaped like the result frame's mono line, so handover swaps values rather than
             * re-laying the row out. Integer seconds: a ticking tenths digit for twenty seconds
             * is noise, and this is only shown once the wait is worth narrating. */}
            <span className="font-mono text-[9px] font-semibold tracking-widest text-white/45 uppercase">
              stun · run 01
              {elapsedSeconds !== undefined && ` · ${elapsedSeconds}s`}
              {liveCandidates.length > 0 &&
                ` · ${liveCandidates.length} candidate${liveCandidates.length === 1 ? "" : "s"}`}
              {" · probe runs locally"}
            </span>
          </div>
        </div>
      </Card>

      {/* The log card, holding page height. Live rows land above the remaining ghost slots:
       * each candidate the visitor watches arrive here is the exact row the report keeps. */}
      <Card className="gap-4 px-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={HEADER}>
            Network path log{liveCandidates.length > 0 && ` (${liveCandidates.length})`}
          </span>
          <Bar className="h-4 w-36" />
        </div>
        {liveCandidates.length > 0 && (
          <ul className="flex flex-col">
            {liveCandidates.map((c) => (
              <IceCandidateRow key={`${c.raw}-${c.atMs}`} candidate={c} />
            ))}
          </ul>
        )}
        {ghostRows > 0 && (
          <div className="flex flex-col gap-2">
            {LOG_ROWS.slice(0, ghostRows).map((row) => (
              <Bar key={row} className="h-7" />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
